import { StateCreator } from 'zustand'
import { mercuryApi } from '$api'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { SharedSlice } from '../../../../../slice'
import { PayClassification, transformPayClassificationOnGet } from '$models'
import { formatCurrency } from 'src/components/routes/_common/helpers'

interface EditPayClassificationState {
  payClassification: PayClassification | null
}

export interface EditPayClassificationSlice extends EditPayClassificationState {
  /**
   * Reset PayClassification Slice
   */
  resetEditPayClassificationSlice: () => void

  /**
   * Get Pay Classification By id
   * @param payClassificationId PayClassification id
   */
  getPayClassification: (payClassificationId: string) => Promise<void>

  /**
   * Update PayClassification
   * @param payClassification PayClassification to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  updatePayClassification: (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: EditPayClassificationState = {
  payClassification: null,
}

const createEditPayClassificationSlice: StateCreator<
  EditPayClassificationSlice & SharedSlice,
  [],
  [],
  EditPayClassificationSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditPayClassificationSlice: () => set(initialState),

  // State API calls
  getPayClassification: async (payClassificationId: string): Promise<void> => {
    return mercuryApi
      .get<PayClassification>(`/pay-classifications/${payClassificationId}`)
      .then((result) =>
        set(() => ({
          payClassification: transformPayClassificationOnGet(result),
        }))
      )
      .catch((error) => {
        logger.error(
          `Failed to retrieve pay classification ${payClassificationId}`,
          error
        )
        set(() => ({
          payClassification: null,
        }))

        exceptionMessageHandler(error, 'Failed to retrieve pay classification')
      })
  },

  updatePayClassification: async (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPayClassification: PayClassification =
        await mercuryApi.update(
          `/pay-classifications/${payClassification.id}`,
          payClassification
        )

      set(() => ({
        payClassification: {
          ...updatedPayClassification,
          baseHourlyRate: formatCurrency(
            updatedPayClassification.baseHourlyRate as number,
            true
          ),
        },
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      if (!updatedPayClassification) {
        logger.error(`${errorMessage} - updatedPayClassification doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },
})

export default createEditPayClassificationSlice
